import React, { createContext, useContext, useState, useEffect } from 'react';

export type LayoutDensity = 'compact' | 'comfortable' | 'spacious';

interface LayoutContextType {
  density: LayoutDensity;
  setDensity: (density: LayoutDensity) => void;
  isCompact: boolean;
  isComfortable: boolean;
  isSpacious: boolean;
}

const LayoutContext = createContext<LayoutContextType | undefined>(undefined);

export const useLayout = () => {
  const context = useContext(LayoutContext);
  if (!context) {
    throw new Error('useLayout must be used within a LayoutProvider');
  }
  return context;
};

interface LayoutProviderProps {
  children: React.ReactNode;
}

export const LayoutProvider: React.FC<LayoutProviderProps> = ({ children }) => {
  const [density, setDensity] = useState<LayoutDensity>(() => {
    const saved = localStorage.getItem('layout-density');
    return (saved as LayoutDensity) || 'comfortable';
  });

  useEffect(() => {
    localStorage.setItem('layout-density', density);
  }, [density]);

  const isCompact = density === 'compact';
  const isComfortable = density === 'comfortable';
  const isSpacious = density === 'spacious';

  const value = {
    density,
    setDensity,
    isCompact,
    isComfortable,
    isSpacious,
  };

  return (
    <LayoutContext.Provider value={value}>
      {children}
    </LayoutContext.Provider>
  );
};

// CSS class names for different densities
export const getDensityClasses = (density: LayoutDensity) => {
  switch (density) {
    case 'compact':
      return {
        container: 'p-2 space-y-2',
        card: 'p-3',
        text: 'text-sm',
        heading: 'text-lg',
        button: 'px-3 py-2 text-sm min-h-[44px] touch-manipulation',
        input: 'px-2 py-2 text-sm min-h-[44px]',
        spacing: 'space-y-2',
        margin: 'm-2',
      };
    case 'comfortable':
      return {
        container: 'p-4 space-y-4',
        card: 'p-4',
        text: 'text-base',
        heading: 'text-xl',
        button: 'px-4 py-3 text-base min-h-[44px] touch-manipulation',
        input: 'px-3 py-3 text-base min-h-[44px]',
        spacing: 'space-y-4',
        margin: 'm-4',
      };
    case 'spacious':
      return {
        container: 'p-6 space-y-6',
        card: 'p-6',
        text: 'text-lg',
        heading: 'text-2xl',
        button: 'px-6 py-4 text-lg min-h-[48px] touch-manipulation',
        input: 'px-4 py-4 text-lg min-h-[48px]',
        spacing: 'space-y-6',
        margin: 'm-6',
      };
    default:
      return {
        container: 'p-4 space-y-4',
        card: 'p-4',
        text: 'text-base',
        heading: 'text-xl',
        button: 'px-4 py-3 text-base min-h-[44px] touch-manipulation',
        input: 'px-3 py-3 text-base min-h-[44px]',
        spacing: 'space-y-4',
        margin: 'm-4',
      };
  }
};
